import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import AttachmentPanel from "../components/AttachmentPanel";
import { fmtDate } from "../lib/format";
import { toast } from "sonner";
import { FolderOpen, FileText, Image as ImageIcon, FileSpreadsheet, FolderTree, Download, Eye, Trash2, Filter } from "lucide-react";

const ENTITY_TYPES = [
    { key: "all", label: "All" },
    { key: "voucher", label: "Vouchers" },
    { key: "purchase_order", label: "Purchase Orders" },
    { key: "grn", label: "GRNs" },
    { key: "requisition", label: "Requisitions" },
    { key: "quotation", label: "Quotations" },
    { key: "sales_order", label: "Sales Orders" },
    { key: "payroll_run", label: "Payroll" },
    { key: "project", label: "Projects" },
    { key: "vendor", label: "Vendors" },
    { key: "client", label: "Clients" },
    { key: "employee", label: "Employees" },
    { key: "bank_statement", label: "Bank Stmts" },
    { key: "generic", label: "Generic" },
];

const fmtSize = (n) => {
    if (n < 1024) return `${n} B`;
    if (n < 1048576) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1048576).toFixed(2)} MB`;
};

const iconFor = (ct) => {
    if (!ct) return FileText;
    if (ct.startsWith("image/")) return ImageIcon;
    if (ct.includes("pdf")) return FileText;
    if (ct.includes("sheet") || ct.includes("csv") || ct.includes("excel")) return FileSpreadsheet;
    return FileText;
};

export default function DocumentVault() {
    const [entityType, setEntityType] = useState("all");
    const [q, setQ] = useState("");
    const [files, setFiles] = useState([]);
    const [summary, setSummary] = useState({});
    const [showGeneric, setShowGeneric] = useState(false);

    const refresh = async () => {
        const r = await api.get(entityType === "all" ? "/files/" : `/files/?entity_type=${entityType}`);
        setFiles(r.data.attachments);
        setSummary(r.data.summary);
    };
    useEffect(() => { refresh(); }, [entityType]);

    const filtered = useMemo(() => {
        if (!q) return files;
        const s = q.toLowerCase();
        return files.filter((f) => f.original_filename.toLowerCase().includes(s) || (f.note || "").toLowerCase().includes(s) || f.entity_id.toLowerCase().includes(s));
    }, [files, q]);

    const download = (f) => {
        const token = localStorage.getItem("awc_token");
        window.open(`${API}/files/${f.id}/download${token ? `?auth=${encodeURIComponent(token)}` : ""}`, "_blank");
    };
    const preview = (f) => {
        const token = localStorage.getItem("awc_token");
        window.open(`${API}/files/${f.id}/preview${token ? `?auth=${encodeURIComponent(token)}` : ""}`, "_blank");
    };
    const del = async (f) => {
        if (!confirm(`Delete "${f.original_filename}"? This soft-deletes — actual bytes remain in cloud storage.`)) return;
        await api.delete(`/files/${f.id}`);
        toast.success("Deleted");
        refresh();
    };

    const totalSize = filtered.reduce((s, f) => s + (f.size || 0), 0);

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// DOCUMENT VAULT · CLOUD STORAGE</div>
                    <h1 className="font-display font-black text-3xl flex items-center gap-3"><FolderOpen className="text-[var(--brand-amber)]" size={26} /> Every File, Every Voucher, One Search</h1>
                    <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                        Persisted in Emergent Managed Cloud Storage · Available on every device the app runs on
                    </div>
                </div>
                <button data-testid="dv-generic-upload" onClick={() => setShowGeneric(true)} className="px-3 py-1.5 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-[10px] tracking-[0.15em] font-semibold hover:bg-[var(--bg-panel-hover)] flex items-center gap-2">
                    <FolderTree size={12} /> Upload Loose Document
                </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard testId="dv-count" label="Total Files" value={files.length} format="custom" accent />
                <KpiCard testId="dv-size" label="Total Storage Used" value={fmtSize(files.reduce((s, f) => s + (f.size || 0), 0))} format="custom" />
                <KpiCard testId="dv-types" label="Entity Types Attached" value={Object.keys(summary.by_entity || {}).length} format="custom" />
                <KpiCard testId="dv-filtered" label="Matching Filter" value={filtered.length} sub={fmtSize(totalSize)} format="custom" />
            </div>

            <Panel testId="dv-filters" title="Filter" subtitle="Find files across every module">
                <div className="flex flex-wrap gap-2 mb-3">
                    {ENTITY_TYPES.map((t) => (
                        <button
                            key={t.key}
                            data-testid={`dv-tab-${t.key}`}
                            onClick={() => setEntityType(t.key)}
                            className={`px-2.5 py-1 border transition-colors text-[10px] font-mono-fin uppercase tracking-wider ${entityType === t.key ? "border-[var(--brand-amber)] bg-[var(--brand-amber-dim)] text-[var(--brand-amber)]" : "border-panel text-[var(--text-secondary)] hover:border-[var(--brand-amber)]"}`}
                        >
                            {t.label} {summary.by_entity && summary.by_entity[t.key] ? <span className="text-[var(--brand-amber)] ml-1">({summary.by_entity[t.key]})</span> : ""}
                        </button>
                    ))}
                </div>
                <div className="relative">
                    <Filter size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                    <input
                        data-testid="dv-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search filename, entity id, or note..."
                        className="bg-[var(--bg-base)] border border-panel pl-7 pr-2 py-1.5 text-xs font-mono-fin text-[var(--text-primary)] w-full max-w-md"
                    />
                </div>
            </Panel>

            <Panel testId="dv-list" title="Files" subtitle={`${filtered.length} shown · ${fmtSize(totalSize)}`}>
                {filtered.length === 0 ? (
                    <div className="text-center py-8 text-[var(--text-secondary)] font-mono-fin text-xs uppercase tracking-wider">
                        No files yet. Upload from any voucher, PO, GRN, project, etc. — or use "Upload Loose Document" above.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel"><tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-2">File</th>
                                <th>Entity</th>
                                <th>Entity ID</th>
                                <th>Uploaded By</th>
                                <th>When</th>
                                <th className="text-right">Size</th>
                                <th></th>
                            </tr></thead>
                            <tbody>
                                {filtered.map((f) => {
                                    const Icon = iconFor(f.content_type);
                                    return (
                                        <tr key={f.id} data-testid={`dv-row-${f.id}`} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                            <td className="py-2">
                                                <div className="flex items-center gap-2">
                                                    <Icon size={14} className="text-[var(--brand-amber)]" />
                                                    <div>
                                                        <div className="text-xs text-[var(--text-primary)] font-body">{f.original_filename}</div>
                                                        {f.note && <div className="text-[10px] font-mono-fin text-[var(--text-muted)] italic">{f.note}</div>}
                                                    </div>
                                                </div>
                                            </td>
                                            <td><span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border border-panel text-[var(--text-secondary)]">{f.entity_type}</span></td>
                                            <td className="text-[var(--brand-amber)] font-mono-fin text-[11px] truncate max-w-[180px]">{f.entity_id || "—"}</td>
                                            <td className="text-[var(--text-secondary)]">{f.uploaded_by || "—"}</td>
                                            <td className="text-[var(--text-secondary)]">{fmtDate(f.created_at)}</td>
                                            <td className="text-right text-[var(--text-primary)]">{fmtSize(f.size)}</td>
                                            <td className="text-right whitespace-nowrap">
                                                <button data-testid={`dv-preview-${f.id}`} onClick={() => preview(f)} className="text-[var(--text-muted)] hover:text-[var(--brand-amber)] px-1"><Eye size={12} /></button>
                                                <button data-testid={`dv-download-${f.id}`} onClick={() => download(f)} className="text-[var(--text-muted)] hover:text-[var(--brand-amber)] px-1"><Download size={12} /></button>
                                                <button data-testid={`dv-delete-${f.id}`} onClick={() => del(f)} className="text-[var(--text-muted)] hover:text-neg px-1"><Trash2 size={12} /></button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>

            {showGeneric && <GenericUploadModal onClose={() => setShowGeneric(false)} onSaved={() => { setShowGeneric(false); refresh(); }} />}
        </div>
    );
}


function GenericUploadModal({ onClose, onSaved }) {
    const [entityId, setEntityId] = useState("");
    const effectiveId = entityId || `generic-${Date.now()}`;

    return (
        <div className="fixed inset-0 z-40 bg-[var(--bg-base)]/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="w-full max-w-2xl bg-panel border border-[var(--brand-amber)]" onClick={(e) => e.stopPropagation()}>
                <div className="border-b border-panel px-4 py-3 flex items-center justify-between">
                    <div className="text-xs font-mono-fin uppercase tracking-[0.15em] text-[var(--brand-amber)]">Upload Loose Document</div>
                    <button onClick={onClose} className="text-[var(--text-secondary)]">✕</button>
                </div>
                <div className="p-4 space-y-3">
                    <div>
                        <label className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">Group / Reference (optional)</label>
                        <input
                            data-testid="dv-generic-id"
                            value={entityId}
                            onChange={(e) => setEntityId(e.target.value)}
                            placeholder="e.g. board-2026-feb-pack, insurance-policies, KYC-vendor-batch"
                            className="w-full bg-[var(--bg-base)] border border-panel px-3 py-2 text-xs font-mono-fin text-[var(--text-primary)] mt-1"
                        />
                        <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)] mt-1">
                            Leave blank to auto-tag. Use a consistent tag to group related docs.
                        </div>
                    </div>
                    <AttachmentPanel entityType="generic" entityId={effectiveId} title="Files" uploadedBy="" />
                    <button data-testid="dv-generic-done" onClick={onSaved} className="w-full px-4 py-2 bg-[var(--brand-amber)] text-black font-mono-fin uppercase text-xs tracking-[0.15em] font-semibold hover:bg-[var(--bg-panel-hover)] mt-2">Done</button>
                </div>
            </div>
        </div>
    );
}
